import React, { useState, useEffect } from 'react';
import axios from '../../utils/axiosInstance';
import CommonForm from '../../components/commonComponent/CommonForm';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import { useNavigate, useParams } from "react-router-dom";

const Guestcreate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formValues, setFormValues] = useState({
    firstName: '',
    lastName: '',
    middleName: '',
    mobileNumber: '',
    email: '',
    businessCategory: '', // single value
    businessSubCategory: '', // comma-separated string
    business_name: '',
    source: '',           // <-- Add this
    invitedByUser: '',    // <-- Add this
  });

  const [dropdowns, setDropdowns] = useState({
    businessCategories: [],
    businessSubCategories: [],
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Fetch business categories
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const businessCategoryRes = await axios.get('/admin/api/businessCategory');
        const businessCategories = businessCategoryRes.data?.businessCategories || [];
        setDropdowns(prev => ({
          ...prev,
          businessCategories: businessCategories.map(bc => ({ value: bc._id, label: bc.name })),
        }));
      } catch (error) {
        toastError('Failed to load business categories');
      }
    };

    fetchDropdownData();
  }, []);

  // Fetch subcategories by category
  const fetchSubCategories = async (categoryId) => {
    try {
      const response = await axios.get(`/admin/api/businessSubCategory?categoryId=${categoryId}`);
      const subCategories = response.data?.businessSubCategories || [];
      setDropdowns(prev => ({
        ...prev,
        businessSubCategories: subCategories.map(sc => ({
          value: sc._id,
          label: sc.name,
          category: sc.category,
        })),
      }));
    } catch (error) {
      toastError("Failed to load subcategories");
    }
  };

  // Fetch users for "Invited User" dropdown (members only)
  const [memberUsers, setMemberUsers] = useState([]);
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const params = new URLSearchParams({
          userType: 'Member',
          page: 1,
          limit: 1000,
        });
        const res = await axios.get(`/api/user?${params.toString()}`);
        setMemberUsers(res.data.users || []);
      } catch (err) {
        setMemberUsers([]);
      }
    };
    fetchMembers();
  }, []);

  // If editing, fetch guest data
  useEffect(() => {
    if (!id) return;

    const fetchUser = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/api/user/${id}`);
        const user = res.data.user;

        if (!user) throw new Error('User not found');

        // Normalize business info for form
        const business = user.business?.[0] || {};
        const businessCategory = business.category
          ? (Array.isArray(business.category)
              ? (business.category[0]?._id || business.category[0])
              : (business.category?._id || business.category))
          : '';
        const businessSubCategory = business.subCategory
          ? (Array.isArray(business.subCategory)
              ? business.subCategory.map(bsc => bsc._id || bsc).join(',')
              : (business.subCategory._id || business.subCategory))
          : '';

        setFormValues({
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          middleName: user.middleName || '',
          mobileNumber: user.mobileNumber || '',
          email: user.email || '',
          businessCategory,
          businessSubCategory,
          business_name: business.name || '',
          source: user.source || '',                // <-- Add this
          invitedByUser: user.invitedByUser || '',  // <-- Add this
        });

        // fetch subcategories for selected category
        if (businessCategory) {
          await fetchSubCategories(businessCategory);
        }
      } catch (error) {
        console.error('Failed to fetch guest:', error);
        toastError('Failed to fetch guest data');
        navigate('/guest');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id, navigate]);

  const handleChange = async (updated) => {
    // If category changed → fetch subcategories
    if (
      updated.businessCategory &&
      updated.businessCategory !== formValues.businessCategory
    ) {
      await fetchSubCategories(updated.businessCategory);
      setFormValues(prev => ({
        ...prev,
        businessCategory: updated.businessCategory,
        businessSubCategory: '', // reset selection
        ...updated,
      }));
      return;
    }

    // If subcategory is changed and is array, join as comma-separated string
    if (updated.businessSubCategory && Array.isArray(updated.businessSubCategory)) {
      setFormValues(prev => ({
        ...prev,
        ...updated,
        businessSubCategory: updated.businessSubCategory.join(','),
      }));
      return;
    }

    setFormValues(prev => ({
      ...prev,
      ...updated,
    }));
  };

  const handleSubmit = async () => {
    const validationRules = {
      firstName: { required: true },
      lastName: { required: true },
      middleName: { required: true },
      mobileNumber: { required: true, mobile: true, pattern: /^\d{10}$/ }, // <-- enforce 10 digits numeric
      email: { required: true },
      businessCategory: { required: true },
      businessSubCategory: { required: true },
      business_name: { required: true },
      source: { required: true },
      invitedByUser: { required: formValues.source === 'user' },
    };

    const validationErrors = validateForm(formValues, validationRules);
    // Custom mobile validation
    if (
      !formValues.mobileNumber ||
      !/^\d{10}$/.test(formValues.mobileNumber)
    ) {
      validationErrors.mobileNumber = 'Mobile number must be exactly 10 digits and numeric only.';
    }
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const businessData = [{
        name: formValues.business_name,
        category: formValues.businessCategory,
        subCategory: formValues.businessSubCategory, // send as comma-separated string
        primaryBusiness: true,
      }];

      const formData = new FormData();
      formData.append('firstName', formValues.firstName || '');
      formData.append('lastName', formValues.lastName || '');
      formData.append('middleName', formValues.middleName || '');
      formData.append('mobileNumber', formValues.mobileNumber || '');
      formData.append('email', formValues.email || '');
      formData.append('business', JSON.stringify(businessData));
      formData.append('userType', 'Guest');
      formData.append('source', formValues.source || ''); // <-- Add this
      formData.append('invitedByUser', formValues.invitedByUser || ''); // <-- Add this

      const config = { headers: { 'Content-Type': 'multipart/form-data' } };

      if (id) {
        await axios.put(`/api/user/${id}`, formData, config);
        toastSuccess('Guest updated successfully!');
      } else {
        await axios.post('/api/user/create', formData, config);
        toastSuccess('Guest created successfully!');
      }

      navigate('/guest');
    } catch (error) {
      let message = 'Failed to save Guest';

      if (error.response) {
        const data = error.response.data;

        if (data.message) {
          message = data.message;
        } else if (data.errors) {
          message = Object.values(data.errors).flat().join(', ');
        } else if (data.error) {
          message = data.error;
        }
      } else if (error.request) {
        message = 'No response from server. Please try again.';
      } else {
        message = error.message;
      }

      toastError(message);
    }
  };

  const handleCancel = () => {
    setFormValues({
      firstName: '',
      lastName: '',
      middleName: '',
      mobileNumber: '',
      email: '',
      businessCategory: '',
      businessSubCategory: '',
      business_name: '',
      source: '',
      invitedByUser: '',
    });
    setErrors({});
  };

  const businessFields = [
    {
      name: 'business_name',
      label: 'Business Name',
      placeholder: 'Enter Business Name',
      type: 'text',
      required: true
    },
    {
      name: 'businessCategory',
      label: 'Business Category',
      placeholder: 'Select Business Category',
      type: 'select',
      options: dropdowns.businessCategories,
      required: true,
      isMulti: false // single select
    },
    {
      name: 'businessSubCategory',
      label: 'Business Subcategory',
      placeholder: 'Select Business Subcategory',
      type: 'select',
      options: dropdowns.businessSubCategories,
      isMulti: true, // multi-select
      required: true
    },
    {
      name: 'source',
      label: 'Invite By',
      type: 'select',
      options: [
        { value: 'socialMedia', label: 'Social Media' },
        { value: 'user', label: 'User' }
      ],
      required: true,
      isMulti: false,
      placeholder: 'Select Invite By'
    },
    {
      name: 'invitedByUser',
      label: 'Invited User',
      type: 'select',
      options: memberUsers.map(u => ({
        value: u._id,
        label: `${u.firstName} ${u.lastName}`
      })),
      required: formValues.source === 'user',
      isMulti: false,
      placeholder: 'Select User',
      hidden: formValues.source !== 'user'
    }
  ];

  const fieldSets = [
    {
      section: 'Personal Details',
      fields: [
        { name: 'firstName', label: 'First Name', placeholder: 'First Name', type: 'text', required: true },
        { name: 'middleName', label: 'Middle Name', placeholder: 'Middle Name', type: 'text', required: true },
        { name: 'lastName', label: 'Last Name', placeholder: 'Last Name', type: 'text', required: true },
      ],
    },
    {
      section: 'Address',
      fields: [
        { name: 'mobileNumber', label: 'Mobile Number', placeholder: 'Mobile Number', type: 'text', required: true, maxLength: 10 },
        { name: 'email', label: 'Email ID', placeholder: 'Email ID', type: 'email', required: true },
      ],
    },
    {
      section: 'Business Details',
      fields: businessFields,
    },
  ];

  // Only include non-hidden fields in each field set
  const visibleFieldSets = fieldSets.map(fs => ({
    ...fs,
    fields: fs.fields.filter(f => !f.hidden),
  }));

  if (loading) return <div>Loading guest data...</div>;

  // Convert businessSubCategory to array for multi-select
  const formValuesForForm = {
    ...formValues,
    businessSubCategory: Array.isArray(formValues.businessSubCategory)
      ? formValues.businessSubCategory
      : (typeof formValues.businessSubCategory === 'string' && formValues.businessSubCategory)
        ? formValues.businessSubCategory.split(',').filter(Boolean)
        : [],
  };

  return (
    <div className="container mt-4">
      <h4 className="mb-3 text-dark border-bottom pb-2">{id ? 'Edit Guest' : 'Guest Registration'}</h4>

      <CommonForm
        title="Guest Registration"
        fields={visibleFieldSets}
        values={formValuesForForm}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        errors={errors}
        requiredFields={[
          'firstName', 'lastName', 'middleName',
          'mobileNumber', 'email', 'businessCategory',
          'businessSubCategory', 'business_name', 'source',
          ...(formValues.source === 'user' ? ['invitedByUser'] : [])
        ]}
        submitLabel={id ? 'Update' : 'Save'}
        cancelLabel="Reset"
      />
    </div>
  );
};

export default Guestcreate;
